/**
 * The one effect catalog, and the manifest → controls projection the generic effect panel renders from.
 *
 * Every room effect is an effect package now, and a package publishes a machine-readable description of
 * its own controls. So the console holds NO table of effect names, subtitles or parameter ranges: the
 * words and the numbers arrive from the package, and adding an effect stops being a console change.
 *
 * The shape here is the controller's `packages.CatalogItem`
 * (`internal/live/effect/packages/catalog.go`), projected verbatim from each package's signed
 * `manifest.json`, and published two ways: beside the command catalog on the device document (cloud) and
 * from `list_effect_packages` (any origin). Audio reactive is projected into the same catalog by the
 * controller as `nuance.audio-reactive`; it is the one card whose controls are not manifest-driven,
 * because its parameters come from the chosen listening preset rather than from a manifest.
 */

import { operatorCopyViolations } from './operatorCopy';

/** Audio reactive's identity in the one catalog (`internal/live/effect/audio_catalog.go`). */
export const AUDIO_REACTIVE_PACKAGE_ID = 'nuance.audio-reactive';
export const AUTO_VJ_PACKAGE_ID = 'nuance.auto-vj';

/** The standard name for the shared "speed relative to the beat" control, which is never an ordinary enum. */
export const MUSIC_RATE_PARAMETER = 'musicRate';

/**
 * Every control kind the published grammar admits.
 *
 * The first four are what a signed effect package declares. The other seven arrived with the audio
 * card's collapse: a built-in audio reaction declares its own controls, and its renderer has always had
 * a richer vocabulary than a WASM manifest — lists of things to choose from, the room's own zones and
 * spans, an ordered choreography, and per-element maps. Owner ruling 2026-09-18: *"why would we not
 * widen the grammar? We need to support everything we have, so widen the grammar if we need to."* So
 * every kind any reaction declares is representable here, and nothing is projected away or hidden.
 */
export type EffectPackageParameterType =
  | 'number' | 'integer' | 'boolean' | 'enum' | 'fixture'
  | 'multiselect' | 'target' | 'targets' | 'target-sequence'
  | 'element-targets' | 'element-limits' | 'element-ratios';
export type EffectPackageAudioInput = 'none' | 'optional' | 'required';
export type EffectPackageOrigin = 'nuance' | 'custom';
/** What an effect package's control holds. */
export type EffectPackageParameterValue = number | boolean | string;
/**
 * What any control in the widened grammar holds.
 *
 * A list for `multiselect` / `targets` / `target-sequence`; a per-element map of lists for
 * `element-targets` / `element-ratios`; a per-element map of whole numbers for `element-limits`. It is
 * the same union the platform validates a stack layer's `parameters` against — a preset's `values`
 * stay flat scalars, because an effect's own knobs are scalars and a reaction's controls belong to the
 * layer that runs it.
 */
export type EffectPackageControlValue =
  | EffectPackageParameterValue
  | string[]
  | Record<string, string[]>
  | Record<string, number>;

/**
 * One move in the planner's vocabulary, as the author of that vocabulary describes it.
 *
 * The per-element kinds are keyed on these identifiers, and the label is the author's own word for the
 * move — carried, never invented, exactly as a package's name is.
 */
export interface EffectParameterElement {
  identifier: string;
  label: string;
}

export interface EffectPackageParameterSpec {
  type: EffectPackageParameterType;
  /** The manifest's declared group name; absent parameters render before named groups. */
  section?: string;
  min?: number;
  max?: number;
  default: EffectPackageControlValue;
  /**
   * The grain the package wants its control moved in, when it has an opinion.
   *
   * `min` and `max` say how far a control goes; they do not say how finely it moves. A speed running
   * 0.4 to 10 wants tenths and a distance running 0 to 20 wants halves, and neither is derivable from
   * the span — so the package declares it, and the console's own ladder is only the fallback for the
   * packages that stay silent.
   */
  step?: number;
  /** The short suffix shown after the readout — "m", "m/s", "×". Absent means the number is unitless. */
  unit?: string;
  values?: string[];
  help?: string;
  /** The refusal text the controller will use, shown as the field's error when it refuses. */
  validate?: string;
  /** The tempo ratios one element may be given. */
  ratios?: string[];
  /**
   * When this control is shown: while any one of these conditions holds. A condition names another
   * control of the same thing and holds while that control is itself shown and either holds one of
   * `in` (an enum, boolean or multiselect — a multiselect while any of `in` is selected) or is greater
   * than `greaterThan` (an integer or number). Display only: a hidden control keeps its value and is
   * still sent.
   *
   * Carried through the cloud as an opaque object and decided here, because the console is the only
   * party that holds the values the rule is about.
   */
  visibleWhen?: EffectVisibilityCondition[];
  /**
   * The control's label and its choices' labels, for a spec the CONSOLE builds from a document that is
   * not a manifest — a tree node's own settings (Auto VJ's Order, Dwell, Transition, S34), which keep the
   * words the console already shows for them. A manifest never carries these and the wire parser never
   * reads them: a package control's label is its parameter name read as English (B17).
   */
  label?: string;
  labels?: string[];
}

export interface EffectPackageSync {
  phase?: string;
  speed?: string;
  trigger?: string;
  /** The one sentence saying what being locked to the music does to THIS effect. */
  description?: string;
  /**
   * Whether a new layer of this package starts locked to the music (manifest/3 `sync.defaultLocked`). Absent
   * means locked, which is what the controller applies to a first-use layer (`audioLayerResolver.firstUseSync`).
   */
  defaultLocked?: boolean;
}

export interface EffectPackageSection {
  name: string;
  collapsed: boolean;
}

export interface EffectPackageItem {
  packageId: string;
  name: string;
  description: string;
  version: string;
  origin: EffectPackageOrigin;
  available: boolean;
  /** Already operator English when the controller writes it. Shown as written, never rephrased. */
  unavailableReason: string;
  state: string;
  /** The already-composed operator string ("Stopped — over budget"). Rendered, never recomposed. */
  stateLabel: string;
  parameters: Record<string, EffectPackageParameterSpec>;
  /**
   * The order the controls are laid out in, as a list of parameter names.
   *
   * A parameter bag is a JSON OBJECT on the wire, and an object has no order an operator can rely on:
   * the controller holds it as a Go map, so `encoding/json` emits it alphabetically and the author's
   * own ordering is gone by the time the console sees it. Rendering straight from `Object.entries` made
   * the screen's layout an accident of serialization. So order is a first-class field, resolved ONCE at
   * parse time: the controller's declared `parameterOrder` when it publishes one, and a deterministic
   * derivation otherwise. Nothing downstream may re-derive it from key iteration.
   */
  parameterOrder: string[];
  /** Declared display order and first-show state; absent on older controller catalogues. */
  sections?: EffectPackageSection[];
  audio: EffectPackageAudioInput;
  audioChannels?: { count: number; consumes: Array<'rms' | 'peak' | 'clipped' | 'pcm'> };
  sync: EffectPackageSync;
}

export type EffectCatalogFilter = 'all' | 'music-reactive';

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function sectionName(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function plainObject(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

const PARAMETER_TYPES: readonly EffectPackageParameterType[] = [
  'number', 'integer', 'boolean', 'enum', 'fixture',
  'multiselect', 'target', 'targets', 'target-sequence',
  'element-targets', 'element-limits', 'element-ratios',
];

/** The kinds whose value is a per-element map rather than one thing the operator chose. */
const ELEMENT_TYPES: readonly EffectPackageParameterType[] = [
  'element-targets', 'element-limits', 'element-ratios',
];

function stringList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((entry): entry is string => typeof entry === 'string');
}

/**
 * What a control opens on, read against the kind that declares it.
 *
 * A default of the wrong SHAPE is not carried forward as some other shape: an empty list, an empty map
 * or the kind's own neutral scalar is used instead, because a control handed a string where it expects
 * a list would render the string's characters as chosen items.
 */
function parameterDefault(type: EffectPackageParameterType, declared: unknown): EffectPackageControlValue {
  if (type === 'multiselect' || type === 'targets' || type === 'target-sequence') {
    return stringList(declared) ?? [];
  }
  if (type === 'element-limits') {
    const map: Record<string, number> = {};
    for (const [key, entry] of Object.entries(plainObject(declared) ?? {})) {
      if (typeof entry === 'number' && Number.isFinite(entry)) map[key] = entry;
    }
    return map;
  }
  if (type === 'element-targets' || type === 'element-ratios') {
    const map: Record<string, string[]> = {};
    for (const [key, entry] of Object.entries(plainObject(declared) ?? {})) {
      const list = stringList(entry);
      if (list) map[key] = list;
    }
    return map;
  }
  if (type === 'boolean') return typeof declared === 'boolean' ? declared : false;
  if (type === 'enum' || type === 'target') return typeof declared === 'string' ? declared : '';
  return typeof declared === 'number' && Number.isFinite(declared) ? declared : 0;
}

function parameterSpec(value: unknown): EffectPackageParameterSpec | undefined {
  const raw = plainObject(value);
  if (!raw) return undefined;
  const declaredType = optionalString(raw.type);
  const type = PARAMETER_TYPES.find((candidate) => candidate === declaredType);
  if (!type) return undefined;
  const values = stringList(raw.values);
  const ratios = stringList(raw.ratios);
  const visibleWhen = visibilityRule(raw.visibleWhen);
  // A step is only a step if it is positive; a zero or negative one would freeze or invert the control,
  // so it is treated as undeclared and the ladder answers instead.
  const step = finiteNumber(raw.step);
  return {
    type,
    ...(sectionName(raw.section) ? { section: sectionName(raw.section) } : {}),
    ...(finiteNumber(raw.min) != null ? { min: finiteNumber(raw.min) } : {}),
    ...(finiteNumber(raw.max) != null ? { max: finiteNumber(raw.max) } : {}),
    default: parameterDefault(type, raw.default),
    ...(step != null && step > 0 ? { step } : {}),
    ...(optionalString(raw.unit) ? { unit: optionalString(raw.unit) } : {}),
    ...(values?.length ? { values } : {}),
    ...(optionalString(raw.help) ? { help: optionalString(raw.help) } : {}),
    ...(optionalString(raw.validate) ? { validate: optionalString(raw.validate) } : {}),
    ...(ratios?.length ? { ratios } : {}),
    ...(visibleWhen ? { visibleWhen } : {}),
  };
}

/** One condition of a manifest `visibleWhen`: exactly one of `in` and `greaterThan`. */
export interface EffectVisibilityCondition {
  parameter: string;
  in?: (string | boolean)[];
  greaterThan?: number;
}

/**
 * A `visibleWhen` is one condition or a list of one to eight alternatives; either way it is read as the list.
 *
 * It is read by the manifest grammar's rules (plan Amendment w; rule sheet
 * `derivations/effect-package-3/expected.json`): a condition names a control and holds EXACTLY ONE of `in`
 * (a non-empty list of strings or booleans) and `greaterThan` (a finite number), and nothing else. A rule that
 * breaks them is refused — `readVisibilityRule` says why — and the control then carries no rule: the
 * controller refuses such a manifest at admission, so the console never shows the operator less than the
 * package can do on a rule it cannot read.
 */
export function readVisibilityRule(raw: unknown): { conditions: EffectVisibilityCondition[] } | { refused: string } {
  const items = Array.isArray(raw) ? raw : [raw];
  if (items.length === 0 || items.length > 8) return { refused: 'visibleWhen must hold one to eight conditions' };
  const conditions: EffectVisibilityCondition[] = [];
  for (const item of items) {
    const rule = plainObject(item);
    if (!rule || typeof rule.parameter !== 'string' || !rule.parameter) {
      return { refused: 'a visibleWhen condition names a control' };
    }
    if (Object.keys(rule).some((key) => key !== 'parameter' && key !== 'in' && key !== 'greaterThan')) {
      return { refused: 'a visibleWhen condition holds only parameter, in and greaterThan' };
    }
    if ((rule.in === undefined) === (rule.greaterThan === undefined)) {
      return { refused: 'a visibleWhen condition needs exactly one of in and greaterThan' };
    }
    if (rule.greaterThan !== undefined) {
      if (typeof rule.greaterThan !== 'number' || !Number.isFinite(rule.greaterThan)) {
        return { refused: 'visibleWhen.greaterThan is a number' };
      }
      conditions.push({ parameter: rule.parameter, greaterThan: rule.greaterThan });
      continue;
    }
    if (!Array.isArray(rule.in) || rule.in.length === 0) return { refused: 'visibleWhen.in must list at least one value' };
    if (!rule.in.every((value) => typeof value === 'string' || typeof value === 'boolean')) {
      return { refused: 'visibleWhen.in holds strings or booleans' };
    }
    conditions.push({ parameter: rule.parameter, in: rule.in as (string | boolean)[] });
  }
  return { conditions };
}

function visibilityRule(raw: unknown): EffectVisibilityCondition[] | undefined {
  if (raw === undefined) return undefined;
  const read = readVisibilityRule(raw);
  return 'conditions' in read ? read.conditions : undefined;
}

/**
 * Whether one control is shown, given what every control on the same thing is set to.
 *
 * A control with no rule is shown. A control with a rule is shown while any one of its conditions
 * holds: the control the condition names is itself shown, and it holds one of the allowed values (a
 * multiselect: any of them is among the selected ones; a boolean: its true/false is listed) or is
 * greater than the condition's value. A rule that would come back round to a control it is deciding
 * (the controller refuses such a manifest at admission) reads as hidden rather than recursing.
 */
export function parameterInPlay(
  name: string,
  parameters: Record<string, EffectPackageParameterSpec>,
  values: Record<string, EffectPackageControlValue>,
  deciding: ReadonlySet<string> = new Set(),
): boolean {
  const conditions = parameters[name]?.visibleWhen;
  if (!conditions) return true;
  if (deciding.has(name)) return false;
  const inside = new Set(deciding).add(name);
  return conditions.some((condition) => {
    if (!parameterInPlay(condition.parameter, parameters, values, inside)) return false;
    const held = values[condition.parameter] ?? parameters[condition.parameter]?.default;
    if (condition.greaterThan != null) return typeof held === 'number' && held > condition.greaterThan;
    const members: (string | boolean)[] = Array.isArray(held)
      ? held
      : typeof held === 'string' || typeof held === 'boolean' ? [held] : [];
    return (condition.in ?? []).some((allowed) => members.includes(allowed));
  });
}

/**
 * The group a parameter belongs to, which is what decides its place when the controller declares no
 * order of its own. Lower sorts higher on screen.
 *
 * This is the SDK's documented group order — the shape every shipped manifest already writes by hand:
 * the brightness pair first, because it is the control an operator reaches for before any other; then
 * the effect's other `minX`/`maxX` pairs, which are structure rather than a knob you ride; then its
 * lone motion and geometry numbers; then its enums and switches, which are settings; and `musicRate`
 * last and alone, because it belongs beside the sync switch and never appears among these at all.
 */
function parameterGroup(
  name: string,
  parameters: Record<string, EffectPackageParameterSpec>,
): number {
  if (name === MUSIC_RATE_PARAMETER) return 5;
  if (/^(min|max)Brightness$/.test(name)) return 0;
  const spec = parameters[name];
  const paired = /^min[A-Z]/.test(name)
    ? pairedMaximum(name, parameters) != null
    : /^max[A-Z]/.test(name) && pairedMaximum(`min${name.slice('max'.length)}`, parameters) === name;
  if (paired) return 1;
  if (spec.type === 'number' || spec.type === 'integer') return 2;
  if (spec.type === 'enum') return 3;
  if (spec.type === 'boolean') return 4;
  // The list, target and per-element kinds last: each is several rows tall, and burying the knobs an
  // operator rides underneath a per-element map would put the small decisions below the big ones.
  return 6;
}

/**
 * The order to lay a package's controls out in, when the controller published none.
 *
 * The key order of a `parameters` bag carries NO information by the time it reaches the console. The
 * catalog rides to a cloud console as a Firestore map field, which is a protobuf `map<string, Value>`
 * with no defined order on the wire, and the Web SDK hands its keys back in a DIFFERENT order on every
 * snapshot — while the daemon rewrites the document every few seconds. So order is either DECLARED, as
 * `parameterOrder`, or derived from the parameter NAMES; never from iteration. Ties inside a group are
 * broken on the name by codepoint, which makes this a total order: the same manifest always produces
 * the same screen, however the bag happened to be walked.
 */
export function derivedParameterOrder(parameters: Record<string, EffectPackageParameterSpec>): string[] {
  return Object.keys(parameters)
    .map((name) => ({ name, group: parameterGroup(name, parameters) }))
    .sort((left, right) => left.group - right.group
      || (left.name < right.name ? -1 : left.name > right.name ? 1 : 0))
    .map((entry) => entry.name);
}

/**
 * The declared order, accepted only when it accounts for EXACTLY the parameters the card carries.
 *
 * A list that names a parameter the card does not declare, or omits one it does, would silently drop a
 * control off the screen or render one with no spec behind it. Either is worse than falling back to the
 * derived order, so a list that does not match is refused whole.
 */
function declaredParameterOrder(
  value: unknown,
  parameters: Record<string, EffectPackageParameterSpec>,
): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const names = value.filter((entry): entry is string => typeof entry === 'string');
  if (names.length !== Object.keys(parameters).length) return undefined;
  if (new Set(names).size !== names.length) return undefined;
  return names.every((name) => name in parameters) ? names : undefined;
}

/**
 * The order to lay THIS card's controls out in.
 *
 * `parameterOrder` is resolved once at parse time, but a card can also be built in memory — anything
 * that spreads one and replaces its parameters — and a list that no longer accounts for exactly the
 * parameters beside it would silently drop a control off the screen. So the check the wire gets is
 * applied again here, at the ONE place order is consumed, and a list that does not match falls back to
 * the derived order rather than losing a knob.
 */
export function effectPackageParameterOrder(item: EffectPackageItem): string[] {
  return declaredParameterOrder(item.parameterOrder, item.parameters)
    ?? derivedParameterOrder(item.parameters);
}

/**
 * Parse one published catalog entry.
 *
 * Deliberately TOLERANT about fields it does not know: a controller that adds a field must not cost the
 * console the whole card. Only `packageId` is load-bearing, because without it there is nothing to start,
 * nothing to key persisted values against, and nothing to match against the running effect.
 */
export function parseEffectPackage(value: unknown): EffectPackageItem | undefined {
  const raw = plainObject(value);
  if (!raw) return undefined;
  const packageId = optionalString(raw.packageId) ?? optionalString(raw.id);
  if (!packageId) return undefined;
  const inputs = plainObject(raw.inputs);
  const audio = optionalString(inputs?.audio);
  const declared = plainObject(raw.parameters) ?? {};
  const parameters: Record<string, EffectPackageParameterSpec> = {};
  for (const [name, spec] of Object.entries(declared)) {
    const parsed = parameterSpec(spec);
    if (parsed) parameters[name] = parsed;
  }
  const sections = Array.isArray(raw.sections)
    ? raw.sections.flatMap((value): EffectPackageSection[] => {
      const section = plainObject(value);
      const name = sectionName(section?.name);
      return name ? [{ name, collapsed: section?.collapsed === true }] : [];
    })
    : undefined;
  // `origin` is derived from the id namespace on the controller and carried on the wire. Deriving it
  // again here means a card still lands in the right group if an older controller omits the field.
  const published = optionalString(raw.origin);
  const origin: EffectPackageOrigin = published === 'nuance' || published === 'custom'
    ? published
    : packageId.startsWith('nuance.') ? 'nuance' : 'custom';
  const channels = plainObject(raw.audioChannels);
  const consumes = channels?.catalog === 'channels/1' && Array.isArray(channels.consumes)
    ? channels.consumes : undefined;
  const audioChannels = Number.isInteger(channels?.count) && Number(channels?.count) >= 1
    && Number(channels?.count) <= 8 && consumes?.length && consumes.length <= 4
    && new Set(consumes).size === consumes.length
    && consumes.every((entry) => ['rms', 'peak', 'clipped', 'pcm'].includes(String(entry)))
    ? { count: Number(channels!.count), consumes: consumes as Array<'rms' | 'peak' | 'clipped' | 'pcm'> }
    : undefined;
  return {
    packageId,
    name: optionalString(raw.name) ?? packageId,
    description: optionalString(raw.description) ?? '',
    version: optionalString(raw.version) ?? '',
    origin,
    available: raw.available !== false,
    unavailableReason: optionalString(raw.unavailableReason) ?? '',
    state: optionalString(raw.state) ?? 'installed',
    stateLabel: optionalString(raw.stateLabel) ?? '',
    parameters,
    parameterOrder: declaredParameterOrder(raw.parameterOrder, parameters)
      ?? derivedParameterOrder(parameters),
    ...(sections ? { sections } : {}),
    audio: audio === 'none' || audio === 'optional' || audio === 'required' ? audio : 'none',
    ...(audioChannels ? { audioChannels } : {}),
    sync: parseSync(raw.sync),
  };
}

function parseSync(value: unknown): EffectPackageSync {
  const sync = plainObject(value) ?? {};
  return {
    ...(optionalString(sync.phase) ? { phase: optionalString(sync.phase) } : {}),
    ...(optionalString(sync.speed) ? { speed: optionalString(sync.speed) } : {}),
    ...(optionalString(sync.trigger) ? { trigger: optionalString(sync.trigger) } : {}),
    ...(optionalString(sync.description) ? { description: optionalString(sync.description) } : {}),
    ...(typeof sync.defaultLocked === 'boolean' ? { defaultLocked: sync.defaultLocked } : {}),
  };
}

/**
 * The room's effect catalog, in the order the controller published it (origin, then case-folded name).
 * A duplicate package id keeps the first entry, which is the selected version the controller lists first.
 */
export function parseEffectCatalog(value: unknown): EffectPackageItem[] {
  if (!Array.isArray(value)) return [];
  const byId = new Map<string, EffectPackageItem>();
  for (const entry of value) {
    const item = parseEffectPackage(entry);
    if (item && !byId.has(item.packageId)) byId.set(item.packageId, item);
  }
  return [...byId.values()];
}

/**
 * Audio reactive's card, whether or not the controller projects it into the effect catalog.
 *
 * A 1.7.14 daemon publishes it as `nuance.audio-reactive` like any other card, and that projection —
 * carrying the controller's own description and its live availability — always wins. But a 1.7.13 daemon
 * publishes the older catalog shape and no such entry, while still registering `start_audio_reactive`.
 * Keying the card on the COMMAND rather than on the catalog is what keeps the room's one always-on
 * effect reachable on both, instead of leaving an operator on an older controller with no way to run it.
 *
 * The synthesized card carries no description: the console does not write copy for an effect, and a
 * daemon that publishes none has not said anything for it to show.
 */
export function audioReactiveCard(
  commandCatalog: unknown,
  projected: EffectPackageItem | undefined,
): EffectPackageItem | undefined {
  if (projected) return projected;
  const commands = plainObject(commandCatalog);
  if (!commands || !('start_audio_reactive' in commands)) return undefined;
  return {
    packageId: AUDIO_REACTIVE_PACKAGE_ID,
    name: 'Audio reactive',
    description: '',
    version: '',
    origin: 'nuance',
    available: true,
    unavailableReason: '',
    state: 'installed',
    stateLabel: '',
    parameters: {},
    parameterOrder: [],
    audio: 'required',
    sync: {},
  };
}

/**
 * The room's effect library as the grid renders it: every published card, with audio reactive present
 * whenever this controller can run it. Ordering follows the controller's own — origin, then name —
 * which puts audio reactive first among ours whether it was projected or synthesized.
 */
export function roomEffectLibrary(
  publishedCatalog: unknown,
  commandCatalog: unknown,
): EffectPackageItem[] {
  const published = parseEffectCatalog(publishedCatalog);
  const projected = published.find((item) => item.packageId === AUDIO_REACTIVE_PACKAGE_ID);
  const card = audioReactiveCard(commandCatalog, projected);
  if (!card || projected) return published;
  return [card, ...published];
}

function sameParameterSpec(
  left: EffectPackageParameterSpec | undefined,
  right: EffectPackageParameterSpec | undefined,
): boolean {
  // A name the order carries but the parameters do not cannot happen through `parseEffectPackage`, which
  // resolves the two together. It is guarded anyway: this decides whether the screen may keep the objects
  // it already rendered, and the safe answer to "I cannot tell" is always "they differ".
  if (!left || !right) return left === right;
  return left.type === right.type
    && left.min === right.min
    && left.max === right.max
    && sameControlValue(left.default, right.default)
    && left.help === right.help
    && left.section === right.section
    && left.validate === right.validate
    && sameStringList(left.values, right.values)
    && sameStringList(left.ratios, right.ratios)
    && JSON.stringify(left.visibleWhen ?? null) === JSON.stringify(right.visibleWhen ?? null);
}

function sameStringList(left: readonly string[] | undefined, right: readonly string[] | undefined): boolean {
  return (left ?? []).length === (right ?? []).length
    && (left ?? []).every((value, index) => value === (right ?? [])[index]);
}

function sameControlValue(left: EffectPackageControlValue, right: EffectPackageControlValue): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left) && Array.isArray(right) && sameStringList(left, right);
  }
  const leftMap = plainObject(left);
  const rightMap = plainObject(right);
  if (leftMap || rightMap) {
    if (!leftMap || !rightMap) return false;
    const keys = Object.keys(leftMap);
    return keys.length === Object.keys(rightMap).length && keys.every((key) => {
      const one = leftMap[key];
      const other = rightMap[key];
      return Array.isArray(one) || Array.isArray(other)
        ? Array.isArray(one) && Array.isArray(other) && sameStringList(one as string[], other as string[])
        : one === other;
    });
  }
  return left === right;
}

function sameEffectPackage(left: EffectPackageItem, right: EffectPackageItem): boolean {
  return left.packageId === right.packageId
    && left.name === right.name
    && left.description === right.description
    && left.version === right.version
    && left.origin === right.origin
    && left.available === right.available
    && left.unavailableReason === right.unavailableReason
    && left.state === right.state
    && left.stateLabel === right.stateLabel
    && left.audio === right.audio
    && left.audioChannels?.count === right.audioChannels?.count
    && sameStringList(left.audioChannels?.consumes, right.audioChannels?.consumes)
    && left.sync.phase === right.sync.phase
    && left.sync.speed === right.sync.speed
    && left.sync.trigger === right.sync.trigger
    && left.sync.description === right.sync.description
    && left.sync.defaultLocked === right.sync.defaultLocked
    && left.parameterOrder.length === right.parameterOrder.length
    && left.parameterOrder.every((name, index) => name === right.parameterOrder[index])
    && (left.sections ?? []).length === (right.sections ?? []).length
    && (left.sections ?? []).every((section, index) => section.name === right.sections?.[index]?.name
      && section.collapsed === right.sections[index].collapsed)
    && left.parameterOrder.every((name) => sameParameterSpec(left.parameters[name], right.parameters[name]));
}

/**
 * Whether two parsed libraries say the same thing.
 *
 * This exists to defend object IDENTITY, which on this screen is load-bearing rather than cosmetic. The
 * controller mirror is a WHOLE-NODE Realtime Database subscription, so every write anywhere under the
 * controller's node — and the daemon writes several times a second — re-delivers the entire node and
 * the console re-parses the catalog into brand-new objects saying exactly what the old ones said.
 * Solid's `<For>` reconciles on reference, so those new objects meant every effect card and every
 * control row was torn out of the DOM and rebuilt twice a second. An operator dragging a slider had the
 * input removed from under their pointer mid-gesture: the drag stopped tracking, and the replacement
 * input rendered at the controller's last-reported value, which read as the thumb snapping backwards.
 *
 * So the catalog memo returns its PREVIOUS array whenever this says nothing changed, and a quiet mirror
 * tick reaches the screen as no work at all.
 */
export function sameEffectLibrary(
  left: readonly EffectPackageItem[],
  right: readonly EffectPackageItem[],
): boolean {
  return left.length === right.length && left.every((item, index) => sameEffectPackage(item, right[index]));
}

/** A card belongs to Music-reactive when the package declares that it can listen at all. */
export function effectPackageUsesMusic(item: EffectPackageItem): boolean {
  return item.audio !== 'none' || item.audioChannels !== undefined;
}

/**
 * The reason a card cannot run, IF an operator may read it.
 *
 * Most of these are written for an operator and are better than anything the console could say — "No
 * music input — pick one on the Music tab" names the thing to go and do. But the same field also carries
 * whatever a failed start threw: a real daemon answered
 * `failed-precondition: effect layout rigVersionId "rv_…" does not match loaded rig "rv_…"`, and the
 * console rendered it on the card. So the string is checked against the copy law before it is shown, and
 * a reason that names our internals falls back to the one sentence with the raw text kept for staff.
 */
export function operatorUnavailableReason(item: EffectPackageItem): string {
  const reason = item.unavailableReason.trim();
  return reason && operatorCopyViolations(reason).length === 0 ? reason : '';
}

export function filterEffectPackages(
  items: readonly EffectPackageItem[],
  query: string,
  filter: EffectCatalogFilter = 'all',
): EffectPackageItem[] {
  const needle = query.trim().toLocaleLowerCase();
  return items.filter((item) => (
    (filter === 'all' || effectPackageUsesMusic(item))
    && (!needle || `${item.name} ${item.description}`.toLocaleLowerCase().includes(needle))
  ));
}

/**
 * The author's own parameter name, read as English: `rippleSpeed` → "Ripple speed".
 *
 * It is a mechanical transform of the word the package already chose, not console-invented copy — the
 * console has no table of names any more, and a control labelled `rippleSpeed` would put a code token on
 * an operator's screen.
 */
export function parameterLabel(name: string): string {
  const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[._-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/** An offered zone's id mapped to the same display name the room drawing uses. */
export type EffectZoneNames = ReadonlyMap<string, string>;

function percent(value: number): string {
  return String(Number((value * 100).toFixed(10)));
}

/**
 * An offered entry, read as English: `zone:shelf-a` → "Shelf a", `breath` → "Breath".
 *
 * The room's targets arrive in the renderer's own addressing grammar, and `zone:` is the grammar's word
 * rather than the venue's: the operator's word for that target is the zone's own display name. A span is
 * that same name followed by its mechanically derived percent range. The VALUE is never touched; this is
 * what the option says, not what it sends.
 *
 * Other punctuation stays alone — a tempo ratio (`1/4`) is already written the only way it can be
 * written, and putting it through a word transform would make it wrong.
 */
export function entryLabel(value: string, zoneNames?: EffectZoneNames): string {
  const zone = /^zone:([A-Za-z0-9][A-Za-z0-9._-]*)(?::span\(([^,()]+),([^,()]+)\))?$/.exec(value);
  if (zone) {
    const name = zoneNames?.get(zone[1]) ?? parameterLabel(zone[1]);
    if (zone[2] == null || zone[3] == null) return name;
    const start = Number(zone[2]);
    const end = Number(zone[3]);
    if (!Number.isFinite(start) || !Number.isFinite(end) || start < 0 || end > 1 || start > end) return value;
    if (start === 0 && end === 1) return name;
    return `${name}, ${percent(start)}–${percent(end)}%`;
  }
  return /^[A-Za-z][A-Za-z0-9_-]*$/.test(value) ? parameterLabel(value) : value;
}

/**
 * A slider step for a declared range: one hundredth of the span, snapped to the 1 / 2 / 5 ladder so the
 * readout lands on round numbers. 0…1 gives 0.01; 0.05…5 gives 0.05; 0…20 gives 0.2.
 *
 * This is the FALLBACK, not the rule. A package that declares `step` has said what grain its control
 * wants, and that answer wins; this ladder only answers for the packages that stay silent.
 */
export function sliderStep(min: number, max: number): number {
  const span = max - min;
  if (!(span > 0)) return 1;
  const raw = span / 100;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalized = raw / magnitude;
  const snapped = normalized >= 7.071 ? 10 : normalized >= 3.162 ? 5 : normalized >= 1.414 ? 2 : 1;
  return Number((snapped * magnitude).toPrecision(2));
}

/** How many decimals a value shows, so a 0.05 step never renders 0.30000000000000004. */
export function stepDecimals(step: number): number {
  return step >= 1 ? 0 : Math.min(4, Math.max(0, -Math.floor(Math.log10(step))));
}

export function formatParameterValue(value: number, step: number): string {
  return value.toFixed(stepDecimals(step));
}

export interface EffectPackageSliderDefinition {
  key: string;
  label: string;
  meaning: string;
  min: number;
  max: number;
  step: number;
  /** The package's own suffix for this number, rendered after the readout. */
  unit?: string;
  validate?: string;
}

export type EffectPackageControl = (
  /** Two parameters named `minX` / `maxX` are ONE control: one edge moves, both values are sent. */
  | { kind: 'range-pair'; key: string; min: EffectPackageSliderDefinition; max: EffectPackageSliderDefinition }
  | { kind: 'slider'; key: string; definition: EffectPackageSliderDefinition }
  | { kind: 'stepper'; key: string; definition: EffectPackageSliderDefinition }
  | { kind: 'segmented'; key: string; label: string; meaning: string; values: string[]; labels?: string[]; validate?: string }
  | { kind: 'select'; key: string; label: string; meaning: string; values: string[]; labels?: string[]; validate?: string }
  | { kind: 'fixture'; key: string; label: string; meaning: string; validate?: string }
  | { kind: 'switch'; key: string; label: string; meaning: string; validate?: string }
  /** Any number of the offered entries, in no order: `multiselect` and `targets`. */
  | { kind: 'checks'; key: string; label: string; meaning: string; values: string[]; labels: string[]; validate?: string }
  /** One ordered run of entries, where repeating an entry is a legitimate choreography. */
  | { kind: 'sequence'; key: string; label: string; meaning: string; values: string[]; labels: string[]; validate?: string }
  /** One list of entries per move: `element-targets` and `element-ratios`. */
  | {
    kind: 'element-checks';
    key: string;
    label: string;
    meaning: string;
    elements: EffectParameterElement[];
    values: string[];
    labels: string[];
    validate?: string;
  }
  /** One whole number per move: `element-limits`. */
  | {
    kind: 'element-counts';
    key: string;
    label: string;
    meaning: string;
    elements: EffectParameterElement[];
    min?: number;
    max?: number;
    validate?: string;
  }
) & { section?: string };

/** A stepper rather than a slider once an integer range is small enough to count. */
const STEPPER_MAX_VALUES = 8;
/** Above this, a segmented control stops fitting and becomes a menu. */
const SEGMENTED_MAX_VALUES = 6;

/**
 * The declared bounds of a numeric parameter, or nothing.
 *
 * There is no fallback pair here on purpose. A numeric spec with no `min`/`max` is a manifest the
 * controller would have refused (`packages/manifest.go` requires both), so it can only reach the console
 * through a malformed catalog — and inventing 0…1 for it renders a control whose whole range is wrong,
 * which the operator then drags and sends to the room. A bound that was never declared is an error, not
 * a number to guess; the caller draws no control at all rather than a confidently wrong one.
 */
function numericBounds(spec: EffectPackageParameterSpec): { min: number; max: number } | undefined {
  if (spec.min == null || spec.max == null) return undefined;
  return { min: spec.min, max: spec.max };
}

function sliderDefinition(
  name: string,
  spec: EffectPackageParameterSpec,
  bounds: { min: number; max: number },
  step: number,
): EffectPackageSliderDefinition {
  return {
    key: name,
    label: spec.label ?? parameterLabel(name),
    meaning: spec.help ?? '',
    min: bounds.min,
    max: bounds.max,
    step,
    ...(spec.unit ? { unit: spec.unit } : {}),
    ...(spec.validate ? { validate: spec.validate } : {}),
  };
}

/** The `maxX` that pairs with a `minX`, when the manifest declares both as the same kind of number. */
function pairedMaximum(name: string, parameters: Record<string, EffectPackageParameterSpec>): string | undefined {
  if (!/^min[A-Z]/.test(name)) return undefined;
  const partner = `max${name.slice('min'.length)}`;
  const minimum = parameters[name];
  const maximum = parameters[partner];
  if (!maximum || maximum.type !== minimum.type) return undefined;
  if (minimum.type !== 'number' && minimum.type !== 'integer') return undefined;
  return partner;
}

/**
 * The entries one of the addressing kinds offers, always including whatever the thing is ALREADY set to.
 *
 * A `target` may hold a zone span (`zone:bench:span(0.2,0.8)`) that is legal and is in no list, and a
 * `targets` list may have been authored in a room whose zones have since been renamed. A menu that
 * silently dropped those would show the operator a blank, or worse a different zone, and the next thing
 * they touched would send it. So the held value is offered beside the room's own entries — the same rule
 * the stack's package pickers follow for a package that has been removed.
 */
function offeredEntries(
  spec: EffectPackageParameterSpec,
  held: EffectPackageControlValue | undefined,
  zoneNames?: EffectZoneNames,
): string[] {
  const declared = spec.type === 'multiselect'
    ? spec.values ?? []
    : spec.type === 'element-ratios'
      ? spec.ratios ?? []
      : [];
  const entries = [...declared];
  for (const value of heldEntries(spec.default)) if (!entries.includes(value)) entries.push(value);
  if (spec.type === 'target' || spec.type === 'targets' || spec.type === 'target-sequence'
    || spec.type === 'element-targets') {
    for (const zone of zoneNames?.keys() ?? []) {
      const value = `zone:${zone}`;
      if (!entries.includes(value)) entries.push(value);
    }
  }
  for (const value of heldEntries(held)) if (!entries.includes(value)) entries.push(value);
  return entries;
}

/** Every entry a held value names, whatever shape the kind holds it in. */
function heldEntries(held: EffectPackageControlValue | undefined): string[] {
  if (typeof held === 'string') return held ? [held] : [];
  if (Array.isArray(held)) return held;
  const map = plainObject(held);
  if (!map) return [];
  return Object.values(map).flatMap((entry) => (Array.isArray(entry) ? entry as string[] : []));
}

/**
 * One control for the kinds that address the room, a vocabulary, or the planner's moves.
 *
 * `target` is one menu. `multiselect` and `targets` are a set of entries chosen in no order.
 * `target-sequence` is an ORDERED run in which repeating an entry is meaningful, so it is a list the
 * operator builds rather than a set they tick. `element-targets` / `element-ratios` are one set per
 * move, and `element-limits` is one whole number per move.
 */
function addressingControl(
  name: string,
  spec: EffectPackageParameterSpec,
  held: EffectPackageControlValue | undefined,
  zoneNames?: EffectZoneNames,
): EffectPackageControl | undefined {
  const label = parameterLabel(name);
  const meaning = spec.help ?? '';
  const validate = spec.validate ? { validate: spec.validate } : {};
  if (spec.type === 'element-limits') {
    const elements = (spec.values ?? []).map((identifier) => (
      { identifier, label: parameterLabel(identifier) }
    ));
    if (!elements.length) return undefined;
    return {
      kind: 'element-counts',
      key: name,
      label,
      meaning,
      elements,
      ...(spec.min != null ? { min: spec.min } : {}),
      ...(spec.max != null ? { max: spec.max } : {}),
      ...validate,
    };
  }
  const values = offeredEntries(spec, held, zoneNames);
  if (!values.length) return undefined;
  if (spec.type === 'element-targets' || spec.type === 'element-ratios') {
    const elements = (spec.values ?? []).map((identifier) => (
      { identifier, label: parameterLabel(identifier) }
    ));
    if (!elements.length) return undefined;
    return { kind: 'element-checks', key: name, label, meaning, elements, values, labels: values.map((value) => entryLabel(value, zoneNames)), ...validate };
  }
  if (spec.type === 'target') {
    return { kind: 'select', key: name, label, meaning, values, labels: values.map((value) => entryLabel(value, zoneNames)), ...validate };
  }
  if (spec.type === 'target-sequence') {
    return { kind: 'sequence', key: name, label, meaning, values, labels: values.map((value) => entryLabel(value, zoneNames)), ...validate };
  }
  return { kind: 'checks', key: name, label, meaning, values, labels: values.map((value) => entryLabel(value, zoneNames)), ...validate };
}

/**
 * Whether this thing's control list depends on what its controls are SET to.
 *
 * A conditional control or an addressing control must be re-derived when its held value moves.
 */
export function effectControlsFollowValues(item: EffectPackageItem): boolean {
  return Object.values(item.parameters).some((spec) => (
    spec.visibleWhen != null
    || spec.type === 'target' || spec.type === 'targets' || spec.type === 'target-sequence'
    || spec.type === 'multiselect' || ELEMENT_TYPES.includes(spec.type)
  ));
}

/**
 * Manifest → controls, for everything except the shared sync surface.
 *
 * `musicRate` is never here: the shared sync surface selects one of the controller's six intervals.
 *
 * The parameter the manifest says the music clock takes over (`sync.speed`) stays in this list. Its
 * declared row remains visible and becomes inert while the lock drives it.
 */
export function effectPackageControls(
  item: EffectPackageItem,
  values?: Record<string, EffectPackageControlValue>,
  zoneNames?: EffectZoneNames,
): EffectPackageControl[] {
  const parameters = item.parameters;
  const consumed = new Set<string>([MUSIC_RATE_PARAMETER]);
  // Pair the edges BEFORE walking the list: the derived order sorts a group by name, which puts
  // `maxBrightness` before its own `minBrightness`, and a declared one may lead with either edge.
  // Whichever edge comes first is where the one range control lands; it always draws minimum above
  // maximum. Pairing during the walk instead would split the pair into two lone sliders whenever the
  // maximum led.
  const order = effectPackageParameterOrder(item);
  const pairedWith = new Map<string, string>();
  for (const name of order) {
    const partner = pairedMaximum(name, parameters);
    // A pair is one row and cannot belong to two declared sections.
    if (partner && parameters[name].section === parameters[partner].section) pairedWith.set(name, partner);
  }
  const controls: EffectPackageControl[] = [];
  for (const name of order) {
    const spec = parameters[name];
    if (!spec || consumed.has(name)) continue;
    // A control the thing itself says is not in play is not DRAWN. Not greyed out, not a placeholder:
    // owner ruling 2026-09-18, and the same reasoning as B17's `sync.speed` rule from the other side —
    // there is nothing here for an operator to read, so there is nothing to put on the screen.
    if (values && !parameterInPlay(name, parameters, values)) {
      consumed.add(name);
      continue;
    }
    const minimumName = pairedWith.has(name)
      ? name
      : [...pairedWith].find(([, maximumName]) => maximumName === name)?.[0];
    const maximumName = minimumName ? pairedWith.get(minimumName) : undefined;
    if (minimumName && maximumName && !consumed.has(minimumName) && !consumed.has(maximumName)) {
      consumed.add(minimumName);
      consumed.add(maximumName);
      const minimum = parameters[minimumName];
      const maximum = parameters[maximumName];
      const minimumBounds = numericBounds(minimum);
      const maximumBounds = numericBounds(maximum);
      // An edge with no declared bounds takes the whole pair down with it: half a range control is not a
      // control, and the two edges only mean anything read against each other.
      if (!minimumBounds || !maximumBounds) continue;
      // One step for both edges, so the two sliders move in the same increments and the readouts line up.
      // A declared step wins over the ladder, and when both edges declare one the finer of the two is
      // taken — neither edge should end up coarser than its own package asked for.
      const declared = [minimum.step, maximum.step].filter((value): value is number => value != null);
      const step = declared.length
        ? Math.min(...declared)
        : sliderStep(
          Math.min(minimumBounds.min, maximumBounds.min),
          Math.max(minimumBounds.max, maximumBounds.max),
        );
      controls.push({
        kind: 'range-pair',
        key: minimumName,
        min: sliderDefinition(minimumName, minimum, minimumBounds, step),
        max: sliderDefinition(maximumName, maximum, maximumBounds, step),
      });
      continue;
    }
    consumed.add(name);
    if (spec.type === 'fixture') {
      controls.push({ kind: 'fixture', key: name, label: spec.label ?? parameterLabel(name),
        meaning: spec.help ?? '', ...(spec.validate ? { validate: spec.validate } : {}) });
      continue;
    }
    if (spec.type === 'boolean') {
      controls.push({
        kind: 'switch',
        key: name,
        label: spec.label ?? parameterLabel(name),
        meaning: spec.help ?? '',
        ...(spec.validate ? { validate: spec.validate } : {}),
      });
      continue;
    }
    if (spec.type === 'enum') {
      const choices = spec.values ?? [];
      if (!choices.length) continue;
      controls.push({
        kind: choices.length <= SEGMENTED_MAX_VALUES ? 'segmented' : 'select',
        key: name,
        label: spec.label ?? parameterLabel(name),
        meaning: spec.help ?? '',
        values: choices,
        ...(spec.labels?.length === choices.length ? { labels: spec.labels } : {}),
        ...(spec.validate ? { validate: spec.validate } : {}),
      });
      continue;
    }
    if (spec.type === 'target' || spec.type === 'targets' || spec.type === 'target-sequence'
      || spec.type === 'multiselect' || ELEMENT_TYPES.includes(spec.type)) {
      const control = addressingControl(name, spec, values?.[name], zoneNames);
      // A control whose offered entries are empty draws nothing: the targets a room supplies are the
      // whole content of the control, and a catalog published with no room in hand has not said this
      // reaction has no targets — it has said nothing. An empty box is a claim; silence is the truth.
      if (control) controls.push(control);
      continue;
    }
    const bounds = numericBounds(spec);
    if (!bounds) continue;
    if (spec.type === 'integer') {
      // A whole-number control moves a whole number at a time unless the package says otherwise.
      const step = spec.step ?? 1;
      const positions = Math.floor((bounds.max - bounds.min) / step) + 1;
      controls.push({
        kind: positions <= STEPPER_MAX_VALUES ? 'stepper' : 'slider',
        key: name,
        definition: sliderDefinition(name, spec, bounds, step),
      });
      continue;
    }
    const step = spec.step ?? sliderStep(bounds.min, bounds.max);
    controls.push({ kind: 'slider', key: name, definition: sliderDefinition(name, spec, bounds, step) });
  }
  return controls.map((control) => {
    const section = parameters[control.key]?.section;
    return section ? { ...control, section } : control;
  });
}

/**
 * The parameter the music clock takes over while Sync is on, as the manifest declares in `sync.speed`.
 *
 * A package that names `musicRate` here names the shared control itself and so takes over nothing extra —
 * correctly, for an effect like Pond ripple whose own `rippleSpeed` still governs how fast a ring travels
 * while the DROPS land on the beat. A package that names one of its OWN parameters leaves its value
 * visible while the lock drives its clock instead.
 */
export function syncReplacedParameter(item: EffectPackageItem): string | undefined {
  const declared = item.sync.speed;
  if (!declared || declared === MUSIC_RATE_PARAMETER) return undefined;
  return item.parameters[declared] ? declared : undefined;
}

/**
 * Every parameter's starting value, from the declaration alone.
 *
 * A list or a per-element map is COPIED rather than shared: the spec belongs to the catalog, which is
 * re-read and compared for identity several times a second, and handing a layer's working values the
 * catalog's own array would let one layer's edit be read back as the catalog's own default.
 */
export function effectPackageDefaults(item: EffectPackageItem): Record<string, EffectPackageControlValue> {
  const values: Record<string, EffectPackageControlValue> = {};
  for (const [name, spec] of Object.entries(item.parameters)) values[name] = copyControlValue(spec.default);
  return values;
}

/**
 * The scalar half of a control bag — which for an effect package is the whole of it.
 *
 * A saved look's `values` are flat scalars, here and in the cloud's own schema: an effect's own knobs
 * are scalars, and a built-in reaction's list-shaped controls belong to the LAYER that runs it, which a
 * look records in its stack half instead. So this is where the two meet, and it is a projection rather
 * than a cast.
 */
export function scalarControlValues(
  values: Record<string, EffectPackageControlValue>,
): Record<string, EffectPackageParameterValue> {
  const scalars: Record<string, EffectPackageParameterValue> = {};
  for (const [name, value] of Object.entries(values)) {
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'string') {
      scalars[name] = value;
    }
  }
  return scalars;
}

/** One control value, detached from whatever held it. */
export function copyControlValue(value: EffectPackageControlValue): EffectPackageControlValue {
  if (Array.isArray(value)) return [...value];
  const map = plainObject(value);
  if (!map) return value;
  const copy: Record<string, string[] | number> = {};
  for (const [key, entry] of Object.entries(map)) {
    copy[key] = Array.isArray(entry) ? [...entry as string[]] : entry as number;
  }
  return copy as Record<string, string[]> | Record<string, number>;
}

/** What the room remembered for one package: the values it was last run with, and how it followed music. */
export interface RememberedEffectSettings {
  parameters: Record<string, EffectPackageParameterValue>;
  sync?: boolean;
  musicRate?: string;
}

/**
 * The values this room last used for a package, so re-opening a card shows the room's own settings rather
 * than resetting to the manifest's defaults.
 *
 * The controller stores them and the platform mirrors them onto the room document. ONE shape is read —
 * the one `publishEffectParameters` writes and `effectsync.ParseRoom` reads back, which is the
 * controller's own `ParameterEntry`: the parameter bag under `values`, with `sync` and `musicRate`
 * beside it. Anything else is treated as "nothing remembered", which falls back to the manifest.
 *
 * Reading the bag from anywhere else is what this function got wrong before: it looked under
 * `parameters`, found nothing, and fell through to the entry ITSELF — whose own scalars (`version`,
 * `updatedAt`, `updatedBy`) are not parameters, and whose `values` object matched no branch and was
 * dropped. Every stopped card then showed manifest defaults, and Start wrote that display back.
 */
export function rememberedEffectSettings(
  stored: unknown,
  packageId: string,
): RememberedEffectSettings | undefined {
  const all = plainObject(stored);
  const entry = plainObject(all?.[packageId]);
  if (!entry) return undefined;
  const parameters: Record<string, EffectPackageParameterValue> = {};
  for (const [name, value] of Object.entries(plainObject(entry.values) ?? {})) {
    // Keep an unrecognized stored rate visible; only the controller decides whether it can run it.
    if (name === MUSIC_RATE_PARAMETER && (typeof value !== 'string' || !value)) continue;
    if (typeof value === 'number' && Number.isFinite(value)) parameters[name] = value;
    else if (typeof value === 'boolean' || typeof value === 'string') parameters[name] = value;
  }
  const rate = entry.musicRate ?? parameters[MUSIC_RATE_PARAMETER];
  return {
    parameters,
    ...(typeof entry.sync === 'boolean' ? { sync: entry.sync } : {}),
    ...(typeof rate === 'string' && rate ? { musicRate: rate } : {}),
  };
}

/** The live effect-package readout on the controller's state mirror. */
export interface EffectPackageReadout {
  effectId: string;
  packageId: string;
  /** The package's own name, as the controller publishes it beside the id. Never a slug of the id. */
  name: string;
  version: string;
  running: boolean;
  parameters: Record<string, EffectPackageParameterValue>;
  state: string;
  stateLabel: string;
  sync?: { on: boolean; mode: string; bpm: number | null; musicRate?: string; locked: boolean };
}

/**
 * Read the controller's generic `effect.package` block.
 *
 * This decoder is deliberately NOT an exact key match against the controller's struct. Its predecessors
 * were, and a field added on the controller silently rejected the WHOLE block — which blanked `effectId`,
 * which turned every parameter edit and the Stop button into a no-op with nothing on screen to show for
 * it. So unknown fields are ignored, missing optional fields degrade to their own absence, and only
 * `packageId` is required. `effectPackages.test.ts` pins that an unknown field changes nothing.
 */
export function effectPackageFromMirror(effect: unknown): EffectPackageReadout | undefined {
  const block = plainObject(effect);
  if (!block) return undefined;
  const running = block.running === true;
  const sync = plainObject(block.sync);
  return packageReadout(
    block.package,
    running,
    running ? optionalString(block.effectId) ?? '' : '',
    sync && {
      on: sync.on === true,
      mode: optionalString(sync.mode) ?? '',
      bpm: finiteNumber(sync.bpm) ?? null,
      musicRate: sync.musicRate,
      locked: sync.locked === true,
    },
  );
}

/**
 * The same readout from one `get_state.effects[]` entry, which is how a console with no cloud session —
 * the controller's own cable console — learns what is running. The clock arrives as `sync` + `clock`
 * there rather than as the mirror's single `sync` block.
 */
export function effectPackageFromLiveEffect(effect: unknown): EffectPackageReadout | undefined {
  const entry = plainObject(effect);
  if (!entry || entry.kind !== 'effect_package') return undefined;
  const clock = plainObject(entry.clock);
  return packageReadout(
    { ...plainObject(entry.package), parameters: plainObject(entry.settings)?.parameters },
    true,
    optionalString(entry.id) ?? optionalString(entry.effectId) ?? '',
    typeof entry.sync === 'boolean' || clock ? {
      on: entry.sync === true,
      mode: optionalString(clock?.mode) ?? '',
      bpm: finiteNumber(clock?.bpm) ?? null,
      musicRate: clock?.musicRate,
      locked: clock?.locked === true,
    } : undefined,
  );
}

function packageReadout(
  card: unknown,
  running: boolean,
  effectId: string,
  sync: { on: boolean; mode: string; bpm: number | null; musicRate: unknown; locked: boolean } | undefined | false,
): EffectPackageReadout | undefined {
  const raw = plainObject(card);
  if (!raw) return undefined;
  const packageId = optionalString(raw.packageId);
  if (!packageId) return undefined;
  const parameters: Record<string, EffectPackageParameterValue> = {};
  for (const [name, value] of Object.entries(plainObject(raw.parameters) ?? {})) {
    if (typeof value === 'number' && Number.isFinite(value)) parameters[name] = value;
    else if (typeof value === 'boolean' || typeof value === 'string') parameters[name] = value;
  }
  return {
    effectId,
    packageId,
    name: optionalString(raw.name) ?? '',
    version: optionalString(raw.version) ?? '',
    running,
    parameters,
    state: optionalString(raw.state) ?? '',
    stateLabel: optionalString(raw.stateLabel) ?? '',
    ...(sync ? {
      sync: {
        on: sync.on,
        mode: sync.mode,
        bpm: sync.bpm,
        ...(typeof sync.musicRate === 'string' && sync.musicRate ? { musicRate: sync.musicRate } : {}),
        locked: sync.locked,
      },
    } : {}),
  };
}

/** True while the controller says audio reactive owns the room. */
export function audioReactiveIsRunning(effect: unknown): boolean {
  const block = plainObject(effect);
  return block?.running === true && block.kind === 'audio_reactive';
}

/** The card each built-in running-effect kind is, as the controller names the kind. */
const BUILT_IN_CARD_BY_KIND: Readonly<Record<string, string>> = {
  audio_reactive: AUDIO_REACTIVE_PACKAGE_ID,
  auto_vj: AUTO_VJ_PACKAGE_ID,
};

/**
 * The card the room is running, from the mirror's effect block and the direct feed's `effects[]`.
 *
 * The two built-in effects run under their own kinds and carry no package block, so their card is read
 * off the kind here; an installed package is its own readout's id. Empty when nothing runs.
 */
export function runningCardId(mirrorEffect: unknown, liveEffects: readonly unknown[]): string {
  const mirror = plainObject(mirrorEffect);
  const mirrorKind = mirror && mirror.running !== false ? optionalString(mirror.kind) : undefined;
  // Audio reactive reports `running: true` explicitly; Auto VJ is running unless it says otherwise.
  const mirrorBuiltIn = mirrorKind === 'audio_reactive'
    ? (audioReactiveIsRunning(mirror) ? AUDIO_REACTIVE_PACKAGE_ID : undefined)
    : mirrorKind ? BUILT_IN_CARD_BY_KIND[mirrorKind] : undefined;
  const live = liveEffects.map((entry) => optionalString(plainObject(entry)?.kind) ?? '');
  for (const kind of ['audio_reactive', 'auto_vj']) {
    if (live.includes(kind) || mirrorBuiltIn === BUILT_IN_CARD_BY_KIND[kind]) return BUILT_IN_CARD_BY_KIND[kind];
  }
  const current = effectPackageFromMirror(mirrorEffect) ?? liveEffects.map(effectPackageFromLiveEffect).find(Boolean);
  return current?.running ? current.packageId : '';
}

/**
 * Every operator-facing string a package puts on screen, checked against the copy law.
 *
 * The console does not rewrite a package's words — they are the author's, they are signed, and a card
 * whose description the console silently suppressed would reach an operator as a bare name. So this is a
 * guard that REPORTS: `effectPackages.test.ts` holds today's known list, and a new violation fails.
 */
export function effectPackageCopyViolations(item: EffectPackageItem): Array<{ where: string; text: string; terms: string[] }> {
  const checks: Array<[string, string]> = [['name', item.name], ['description', item.description]];
  if (item.sync.description) checks.push(['sync.description', item.sync.description]);
  for (const [name, spec] of Object.entries(item.parameters)) {
    if (spec.help) checks.push([`parameters.${name}.help`, spec.help]);
    if (spec.validate) checks.push([`parameters.${name}.validate`, spec.validate]);
    for (const value of spec.values ?? []) checks.push([`parameters.${name}.values`, value]);
  }
  return checks.flatMap(([where, text]) => {
    const terms = operatorCopyViolations(text);
    return terms.length ? [{ where, text, terms }] : [];
  });
}
